/**
 * Anchor API client — org-scoped JWT auth.
 */

import type { AnchorState } from '../types/anchor';

const BASE = (import.meta.env.VITE_API_BASE ?? '/api/v1').replace(/\/$/, '');
const SESSION_KEY = 'anchor.session';

export const COLLECTION_RESOURCES = {
  partners: 'partners',
  capitalChannels: 'capital-channels',
  governmentPathways: 'government-pathways',
  infrastructureSlots: 'infrastructure-slots',
  evidence: 'evidence',
  meetings: 'meetings',
  decisions: 'decisions',
  reviews: 'weekly-reviews',
} as const;

export const DOCUMENT_PATHS = {
  meta: 'meta',
  settings: 'settings',
  tactical: 'tactical',
  strategicNetwork: 'strategicNetwork',
  regulatedStack: 'regulatedStack',
  ecosystem: 'ecosystem',
} as const;

export type CollectionPath = keyof typeof COLLECTION_RESOURCES;
export type DocumentName = keyof typeof DOCUMENT_PATHS;

export type MemberRole = 'owner' | 'admin' | 'member' | 'viewer';

export type Session = {
  accessToken: string;
  refreshToken: string;
  orgId: string;
  userId: string;
  email: string;
  displayName: string;
  memberRole: MemberRole;
  organization?: { id: string; name: string; createdAt?: string; updatedAt?: string };
  profilePictureUrl?: string;
};

type Route =
  | { kind: 'collection'; resource: string }
  | { kind: 'document'; name: string; inner: string }
  | { kind: 'unsupported'; reason: string };

const COLLECTION_KEYS = (Object.keys(COLLECTION_RESOURCES) as CollectionPath[]).sort(
  (a, b) => b.length - a.length,
);
const DOCUMENT_KEYS = (Object.keys(DOCUMENT_PATHS) as DocumentName[]).sort(
  (a, b) => b.length - a.length,
);

export function routeForPath(path: string): Route {
  const exact = COLLECTION_RESOURCES[path as CollectionPath];
  if (exact) return { kind: 'collection', resource: exact };

  for (const key of COLLECTION_KEYS) {
    if (path.startsWith(`${key}.`)) {
      return { kind: 'unsupported', reason: `${path} is inside the ${key} collection` };
    }
  }

  for (const key of DOCUMENT_KEYS) {
    if (path === key) return { kind: 'document', name: DOCUMENT_PATHS[key], inner: '' };
    if (path.startsWith(`${key}.`)) {
      return { kind: 'document', name: DOCUMENT_PATHS[key], inner: path.slice(key.length + 1) };
    }
  }

  return { kind: 'unsupported', reason: `No API route for "${path}"` };
}

export class ApiError extends Error {
  status: number;
  code: string;
  requestId?: string;

  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (!parsed?.accessToken || !parsed?.orgId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session: Session): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

function sessionHeaders(): Record<string, string> {
  const session = loadSession();
  const headers: Record<string, string> = {};
  if (session?.accessToken) {
    headers.authorization = `Bearer ${session.accessToken}`;
  }
  if (session?.orgId) {
    headers['x-org-id'] = session.orgId;
  }
  return headers;
}

function orgPrefix(): string {
  const session = loadSession();
  if (!session?.orgId) throw new ApiError(401, 'UNAUTHORIZED', 'Sign in required');
  return `/orgs/${session.orgId}`;
}

async function parseResponse<T>(res: Response, method: string, path: string): Promise<T> {
  if (res.status === 204) return null as T;
  const text = await res.text();
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : null;
  if (!res.ok) {
    const error = (payload?.error as { code?: string; message?: string; requestId?: string }) ?? {};
    throw new ApiError(
      res.status,
      error.code ?? 'UNKNOWN',
      error.message ?? `${method} ${path} failed with ${res.status}`,
      error.requestId,
    );
  }
  return payload as T;
}

function buildHeaders(auth: boolean, hasBody: boolean): Record<string, string> {
  const headers = auth ? sessionHeaders() : {};
  if (hasBody) headers['content-type'] = 'application/json';
  return headers;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  { auth = true }: { auth?: boolean } = {},
): Promise<T> {
  const hasBody = body !== undefined;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: buildHeaders(auth, hasBody),
      body: hasBody ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new ApiError(0, 'NETWORK', `Cannot reach the Anchor API: ${message}`);
  }

  if (res.status === 401 && auth && loadSession()?.refreshToken) {
    try {
      await refreshSession();
      res = await fetch(`${BASE}${path}`, {
        method,
        headers: buildHeaders(auth, hasBody),
        body: hasBody ? JSON.stringify(body) : undefined,
      });
    } catch {
      clearSession();
      throw new ApiError(401, 'UNAUTHORIZED', 'Session expired — sign in again');
    }
  }

  return parseResponse<T>(res, method, path);
}

function persistAuthResult(result: Session & Record<string, unknown>): Session {
  const session: Session = {
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
    orgId: result.orgId,
    userId: result.userId,
    email: result.email,
    displayName: result.displayName,
    memberRole: result.memberRole,
    organization: result.organization as Session['organization'],
    profilePictureUrl: result.profilePictureUrl
      ? String(result.profilePictureUrl)
      : undefined,
  };
  saveSession(session);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('anchor:session', { detail: session }));
  }
  return session;
}

/** Merge fields into the current session (profile updates). */
export function patchSession(partial: Partial<Session>): Session | null {
  const current = loadSession();
  if (!current) return null;
  const next = { ...current, ...partial };
  saveSession(next);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('anchor:session', { detail: next }));
  }
  return next;
}

export async function signup(input: {
  email: string;
  password: string;
  displayName?: string;
  orgName?: string;
}): Promise<Session> {
  const result = await request<Session>('POST', '/auth/signup', input, { auth: false });
  return persistAuthResult(result);
}

export async function login(input: { email: string; password: string }): Promise<Session> {
  const result = await request<Session>('POST', '/auth/sessions', input, { auth: false });
  return persistAuthResult(result);
}

export async function refreshSession(): Promise<Session> {
  const current = loadSession();
  if (!current?.refreshToken) throw new ApiError(401, 'UNAUTHORIZED', 'No refresh token');
  const result = await request<Session>(
    'POST',
    '/auth/sessions/refresh',
    { refreshToken: current.refreshToken },
    { auth: false },
  );
  return persistAuthResult(result);
}

export function logout(): void {
  clearSession();
}

export type OrgMember = {
  id: string;
  orgId: string;
  userId: string;
  email: string;
  displayName: string;
  role: MemberRole;
  status: string;
};

export type ProfileMe = {
  userId: string;
  orgId: string;
  email: string;
  displayName: string;
  status: string;
  memberRole: MemberRole;
  profilePictureUrl?: string;
  createdAt: string;
  updatedAt: string;
  organization: { id: string; name: string; createdAt?: string; updatedAt?: string };
};

export const api = {
  getState: async (): Promise<AnchorState> => {
    const r = await request<{ state: AnchorState }>('GET', `${orgPrefix()}/state`);
    return r.state;
  },
  importState: (state: AnchorState) => request('POST', `${orgPrefix()}/state/import`, { state }),
  createRecord: <T extends object>(resource: string, record: T) =>
    request<T & { id: string }>('POST', `${orgPrefix()}/${resource}`, record),
  updateRecord: <T extends object>(resource: string, id: string, changes: Partial<T>) =>
    request<T & { id: string }>('PATCH', `${orgPrefix()}/${resource}/${id}`, changes),
  deleteRecord: (resource: string, id: string) =>
    request('DELETE', `${orgPrefix()}/${resource}/${id}`),
  putDocument: (name: string, value: unknown) =>
    request('PUT', `${orgPrefix()}/documents/${name}`, { value }),
  patchDocument: (name: string, path: string, value: unknown) =>
    request<{ name: string; value: unknown }>('PATCH', `${orgPrefix()}/documents/${name}`, {
      path,
      value,
    }),
  audit: (limit = 100) =>
    request<{ items: unknown[] }>('GET', `${orgPrefix()}/audit?limit=${limit}`),
  listMembers: () =>
    request<{ members: OrgMember[]; users: unknown[] }>('GET', `${orgPrefix()}/members`),
  inviteMember: (body: {
    email: string;
    password: string;
    displayName?: string;
    role?: MemberRole;
  }) => request('POST', `${orgPrefix()}/members`, body),
  getMe: () => request<ProfileMe>('GET', `${orgPrefix()}/profile/me`),
  updateMe: (body: { displayName?: string }) =>
    request<ProfileMe>('PATCH', `${orgPrefix()}/profile/me`, body),
  uploadProfilePicture: async (blob: Blob, contentType: string): Promise<ProfileMe> => {
    const path = `${orgPrefix()}/profile/picture`;
    let res: Response;
    try {
      res = await fetch(`${BASE}${path}`, {
        method: 'PUT',
        headers: {
          ...sessionHeaders(),
          'content-type': contentType,
        },
        body: blob,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new ApiError(0, 'NETWORK', `Cannot reach the Anchor API: ${message}`);
    }
    if (res.status === 401 && loadSession()?.refreshToken) {
      try {
        await refreshSession();
        res = await fetch(`${BASE}${path}`, {
          method: 'PUT',
          headers: {
            ...sessionHeaders(),
            'content-type': contentType,
          },
          body: blob,
        });
      } catch {
        clearSession();
        throw new ApiError(401, 'UNAUTHORIZED', 'Session expired — sign in again');
      }
    }
    return parseResponse<ProfileMe>(res, 'PUT', path);
  },
};

export { BASE as API_BASE };
